package com.vamvisions.main.navigation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.vamvisions.main.core.presentation.navigation.Route
import com.vamvisions.main.core.presentation.navigation.RouteParser
import com.vamvisions.main.core.presentation.navigation.toPath
import kotlinx.browser.window
import org.w3c.dom.events.Event

/** Keeps the current [Route] in sync with the browser location through the History API. */
class BrowserHistory {

    var route: Route by mutableStateOf(currentLocationRoute())
        private set

    private val onPopState: (Event) -> Unit = { route = currentLocationRoute() }

    /** Shows [target] in a new history entry. A [fragment] is kept in the URL, for the page to scroll to it. */
    fun navigate(target: Route, fragment: String? = null) {
        val url = target.toPath() + fragment?.let { "#$it" }.orEmpty()
        if (url != window.location.pathname + window.location.hash) {
            window.history.pushState(null, "", url)
        }
        route = target
        window.scrollTo(0.0, 0.0)
    }

    /** Shows [target] in place of the current history entry, so back skips the page being left. */
    fun replace(target: Route) {
        window.history.replaceState(null, "", target.toPath())
        route = target
    }

    internal fun startListening() = window.addEventListener("popstate", onPopState)

    internal fun stopListening() = window.removeEventListener("popstate", onPopState)

    private fun currentLocationRoute(): Route = RouteParser.parse(window.location.pathname)
}

@Composable
fun rememberBrowserHistory(): BrowserHistory {
    val history = remember { BrowserHistory() }
    DisposableEffect(history) {
        history.startListening()
        onDispose { history.stopListening() }
    }
    return history
}
